import "./App.css";
import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Spin, message } from "antd";
import SiteLoader from "./views/components/SiteLoader";
import Loader from "./views/components/loader";
import util from "../src/utils/util"; // Import util
import "./notification";

Spin.setDefaultIndicator(
  <div>
    <Loader />
  </div>
);

const LayoutAdmin = React.lazy(() => import("./views/layout/Layout"));
const Login = React.lazy(() => import("./views/pages/Login"));
const Dashboard = React.lazy(() => import("./views/pages/Dashboard"));
// const Services = React.lazy(() => import("./views/pages/Services"));
// const Industries = React.lazy(() => import("./views/pages/Industries"));
// const Projects = React.lazy(() => import("./views/pages/FeaturedProjects"));
const Blogs = React.lazy(() => import("./views/pages/BlogPage"));
const SupportTerms = React.lazy(() => import("./views/pages/ContactUs"));

function App() {
  const [isLoggedin, setIsLoggedin] = useState(false);
  const [ajxRequesting, setAjxRequesting] = useState(true);

  // Debug function to check auth status
  const debugAuthStatus = () => {
    const token = util.getToken();
    const isLogged = util.isLogged();
    

    // console.log('[APP] === AUTH STATUS DEBUG ===');
    // console.log('[APP] Token value:', token ? `${token.substring(0, 20)}...` : 'None');
    // console.log('[APP] Token length:', token ? token.length : 0);
    // console.log('[APP] Is logged in (util):', isLogged);
    // console.log('[APP] localStorage keys:', Object.keys(localStorage));
    // console.log('[APP] User type:', util.userType());
    // console.log('[APP] User data exists:', !!localStorage.getItem('user'));

    return { token, isLogged };
  };

  useEffect(() => {
    // console.log('🚀 [APP] === INITIAL AUTH CHECK STARTING ===');

    const initializeAuth = () => {
      const { token, isLogged } = debugAuthStatus();

      if (isLogged && token) {
        // console.log('✅ [APP] Valid token found, setting isLoggedin = true');
        setIsLoggedin(true);

        // Check if this is a fresh login or persisted session
        const msgShown = sessionStorage.getItem("msgShown");
        if (!msgShown) {
          // console.log('📢 [APP] Fresh login - showing welcome message');
          message.success("Welcome back!");
          sessionStorage.setItem("msgShown", "yes");
        } else {
          // console.log('📢 [APP] Persisted session - skipping welcome message');
        }
      } else {
        // console.log('❌ [APP] No valid token found, setting isLoggedin = false');
        setIsLoggedin(false);
        sessionStorage.removeItem("msgShown");
      }

      // console.log('✅ [APP] Auth initialization completed');
      setAjxRequesting(false);
    };

    // Small delay to ensure DOM and localStorage are ready
    setTimeout(initializeAuth, 100);
  }, []);

  // Watch for authentication state changes
  useEffect(() => {
    // console.log('📊 [APP] === STATE UPDATE ===');
    // console.log('📊 [APP] isLoggedin:', isLoggedin);
    // console.log('📊 [APP] ajxRequesting:', ajxRequesting);

    if (!ajxRequesting) {
      debugAuthStatus();
    }
  }, [isLoggedin, ajxRequesting]);

  // Listen for storage changes (multi-tab support)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "authorization" || e.key === "user") {
        // console.log('🔄 [APP] Storage changed in another tab:', e.key);
        const { isLogged } = debugAuthStatus();
        setIsLoggedin(isLogged);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Listen for util logout (if called from other components)
  useEffect(() => {
    const checkAuthInterval = setInterval(() => {
      if (!ajxRequesting) {
        const currentAuthStatus = util.isLogged();
        if (currentAuthStatus !== isLoggedin) {
          // console.log('🔄 [APP] Auth state mismatch detected, syncing...');
          // console.log('🔄 [APP] Current state:', isLoggedin, 'Actual state:', currentAuthStatus);
          setIsLoggedin(currentAuthStatus);
        }
      }
    }, 3000); // Check every 3 seconds

    return () => clearInterval(checkAuthInterval);
  }, [isLoggedin, ajxRequesting]);

  // console.log('🎨 [APP] === RENDER CYCLE ===');
  // console.log('🎨 [APP] Rendering with - ajxRequesting:', ajxRequesting, 'isLoggedin:', isLoggedin);

  // Show loader during initial auth check
  if (ajxRequesting) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f0f2f5",
        }}
      >
        <SiteLoader />
      </div>
    );
  }

  return (
    <BrowserRouter basename="/">
      <Suspense
        fallback={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
              backgroundColor: "#f0f2f5",
            }}
          >
            <SiteLoader />
          </div>
        }
      >
        <Routes>
          {/* Login route */}
          <Route
            path="/login"
            element={isLoggedin ? <Navigate to="/" replace /> : <Login />}
          />

          {/* Protected Admin routes */}
          <Route
            path="/"
            element={
              isLoggedin ? <LayoutAdmin /> : <Navigate to="/login" replace />
            }
          >
            <Route index element={<Dashboard />} />
            {/* <Route path="services" element={<Services />} />
            <Route path="industries" element={<Industries />} />
            <Route path="projects" element={<Projects />} /> */}
            <Route path="blogs" element={<Blogs />} />
            <Route path="support-terms" element={<SupportTerms />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
